import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';

import { DentistService } from '../../../services/dentist';
import { UserService } from '../../../services/user';

@Component({
  selector: 'app-dentist',
  templateUrl: './dentist.component.html',
  styleUrls: ['./dentist.component.scss']
})
export class DentistComponent implements OnInit {

  contentReady = false;
  data: any = null;

  selectedCertificates: string[] = [];
  uploadedCertificates: any[] = [];

  isAdmin = false;
  canEdit = false;

  private readonly apiBaseUrl = 'https://backendtest.blueboxpet.com';

  constructor(
    private dentistService: DentistService,
    private userService: UserService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.userService.getAccountType().subscribe({
      next: (res: any) => {
        const accountType = String(res?.type ?? res?.user_type ?? res?.account_type ?? res?.accountType ?? '').trim();
        const userType = String(res?.user_type ?? '').trim().toLowerCase();
        const accessPages = Array.isArray(res?.access_pages) ? res.access_pages.map(String) : [];

        this.isAdmin = accountType === '1' || userType === 'admin';
        this.canEdit = this.isAdmin || accessPages.includes('7');
      },
      error: () => {
        this.isAdmin = false;
        this.canEdit = false;
      }
    });

    this.route.paramMap.subscribe(params => {
      const id = params.get('id');

      if (!id) {
        Swal.fire('خطأ', 'لم يتم العثور على الطبيب', 'error');
        this.contentReady = true;
        return;
      }

      this.getDentistById(+id);
    });
  }

  // =========================
  // GET SINGLE
  // =========================
  getDentistById(id: number): void {
    this.contentReady = false;

    this.dentistService.getById(id).subscribe({
      next: (res: any) => {
        this.data = res?.data || res;

        this.prepareAdditionalCertificates();
        this.prepareUploadedCertificates();

        this.contentReady = true;
      },
      error: () => {
        this.data = null;
        this.selectedCertificates = [];
        this.uploadedCertificates = [];

        Swal.fire('خطأ', 'فشل تحميل بيانات الطبيب', 'error');
        this.contentReady = true;
      }
    });
  }

  // =========================
  // DATA PREPARE
  // =========================
  private prepareAdditionalCertificates(): void {
    this.selectedCertificates = [];

    const certs = this.data?.additional_certificates;

    if (!certs) return;

    if (Array.isArray(certs)) {
      this.selectedCertificates = certs.map(item => String(item).trim()).filter(Boolean);
      return;
    }

    if (typeof certs === 'string') {
      try {
        const parsed = JSON.parse(certs);

        if (Array.isArray(parsed)) {
          this.selectedCertificates = parsed
            .map(item => String(item).trim())
            .filter(Boolean);

          return;
        }
      } catch {
        // fallback split
      }

      this.selectedCertificates = certs
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    }
  }

  private prepareUploadedCertificates(): void {
    const certificates =
      this.data?.certificates ||
      this.data?.uploaded_certificates ||
      this.data?.certificate_files ||
      [];

    if (Array.isArray(certificates)) {
      this.uploadedCertificates = certificates;
      return;
    }

    if (typeof certificates === 'string') {
      try {
        const parsed = JSON.parse(certificates);
        this.uploadedCertificates = Array.isArray(parsed) ? parsed : [];
      } catch {
        this.uploadedCertificates = [];
      }

      return;
    }

    this.uploadedCertificates = [];
  }

  // =========================
  // FILE URL HELPERS
  // نفس طريقة alldentist
  // =========================
  getStorageUrl(path: string | null | undefined): string {
    if (!path) return '';

    let value = String(path).trim();

    if (!value) return '';

    if (value.startsWith('http://') || value.startsWith('https://')) {
      if (value.includes('/storage/app/public/')) {
        return value;
      }

      if (value.includes('/storage/')) {
        return value.replace('/storage/', '/storage/app/public/');
      }

      return value;
    }

    value = value
      .replace(/^\/+/, '')
      .replace(/^storage\/app\/public\//, '')
      .replace(/^storage\//, '')
      .replace(/^app\/public\//, '')
      .replace(/^public\//, '');

    return `${this.apiBaseUrl}/storage/app/public/${value}`;
  }

  getDoctorImageUrl(): string {
    return this.getStorageUrl(
      this.data?.doctor_image_url ||
      this.data?.doctor_image ||
      this.data?.image
    );
  }

  getCvUrl(): string {
    return this.getStorageUrl(
      this.data?.cv_url ||
      this.data?.cv_pdf ||
      this.data?.cv
    );
  }

  getCertificateUrl(cert: any): string {
    return this.getStorageUrl(
      cert?.url ||
      cert?.file_url ||
      cert?.certificate_url ||
      cert?.file_path ||
      cert?.certificate_path ||
      cert?.path
    );
  }

  getPortfolioUrl(link: string | null | undefined): string {
    if (!link) return '';

    const value = String(link).trim();

    if (!value) return '';

    if (value.startsWith('http://') || value.startsWith('https://')) {
      return value;
    }

    return `https://${value}`;
  }

  isImageCertificate(cert: any): boolean {
    const mime = String(cert?.mime_type || '').toLowerCase();
    const type = String(cert?.type || '').toLowerCase();
    const url = this.getCertificateUrl(cert).toLowerCase();

    return (
      type === 'image' ||
      mime.startsWith('image/') ||
      url.endsWith('.jpg') ||
      url.endsWith('.jpeg') ||
      url.endsWith('.png') ||
      url.endsWith('.webp')
    );
  }

  getCertificateName(cert: any, index: number): string {
    return (
      cert?.original_name ||
      cert?.name ||
      cert?.filename ||
      cert?.file_name ||
      `شهادة رقم ${index + 1}`
    );
  }

  getCertificateTypeLabel(cert: any): string {
    if (this.isImageCertificate(cert)) {
      return 'صورة';
    }

    const mime = String(cert?.mime_type || '').toLowerCase();
    const url = this.getCertificateUrl(cert).toLowerCase();

    if (mime.includes('pdf') || url.endsWith('.pdf')) {
      return 'PDF';
    }

    return 'ملف';
  }

  formatFileSize(size: number): string {
    if (!size) return '';

    const kb = size / 1024;

    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }

    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  }

  trackByCertificate(index: number, cert: any): any {
    return cert?.id || cert?.file_path || cert?.path || index;
  }

  // =========================
  // LABEL HELPERS
  // =========================
  genderLabel(val: string): string {
    if (val === 'male') return 'ذكر';
    if (val === 'female') return 'أنثى';

    return '—';
  }

  scfhsStatusLabel(val: string): string {
    if (val === 'yes') return 'مصنف';
    if (val === 'in_progress') return 'قيد الإجراء';
    if (val === 'no') return 'غير مصنف';

    return '—';
  }

  scfhsLevelLabel(val: string): string {
    const map: any = {
      general_dentist: 'General Dentist',
      registrar: 'Registrar',
      resident: 'Resident',
      senior_registrar: 'Senior Registrar',
      consultant: 'Consultant'
    };

    return map[val] || '—';
  }

  yearsLabel(val: string): string {
    const map: any = {
      less_than_1: 'أقل من سنة',
      '1_3': '1 – 3 سنوات',
      '4_7': '4 – 7 سنوات',
      more_than_7: 'أكثر من 7 سنوات'
    };

    return map[val] || '—';
  }

  employmentLabel(val: string): string {
    if (val === 'full_time') return 'دوام كامل';
    if (val === 'part_time') return 'دوام جزئي';
    if (val === 'open') return 'كلاهما';

    return '—';
  }

  practiceEnvLabel(val: string): string {
    if (val === 'hospital') return 'مستشفى';
    if (val === 'dental_clinic') return 'عيادة أسنان';

    return '—';
  }

  interpretationLabel(val: string): string {
    if (val === 'priority_shortlist') return 'أولوية';
    if (val === 'hold_further_review') return 'مراجعة';
    if (val === 'not_suitable') return 'غير مناسب';

    return '—';
  }

  // =========================
  // HELPERS
  // =========================
  isSurgerySpecialty(): boolean {
    if (!this.data?.specialty) return false;

    const specialty = String(this.data.specialty).toLowerCase();

    const surgery = [
      'oral surgery',
      'oral & maxillofacial surgery',
      'oral & maxillofacial surgery (omfs)'
    ];

    return surgery.some(s => specialty.includes(s));
  }

  // =========================
  // ACTIONS
  // =========================
  edit(): void {
    if (!this.data?.id) return;

    this.router.navigate(['/dentistform'], {
      queryParams: { id: this.data.id }
    });
  }
}
